import { useState, useEffect, useCallback, useRef } from "react";
import { analyticsApi } from "../services/analyticsApi";

const REFRESH_MS = 5 * 60 * 1000;
const REALTIME_MS = 30 * 1000;

export function useAnalytics(dateParams = {}) {
  const { start, end } = dateParams;

  const [data, setData] = useState({
    overview: null, channels: null, channelTotals: null,
    devices: null, locations: null, realtime: null, pages: null,
    realtimeOverview: null, audience: null, demographics: null,
    acquisition: null, pagesOverview: null, events: null,
    weeklyTrend: null, channelsByDevice: null,
    overviewPerPub: null, channelsPerPub: null, audiencePerPub: null,
    engagementPerPub: null, technologyPerPub: null, realtimePerPub: null,
    dailyViewsPerPub: null, hourlyViewsPerPub: null,
  });
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchAll = useCallback(async () => {
    const p = start ? { start, end } : {};

    const ok = r => r.status === "fulfilled" ? r.value : null;

    const [
      overviewR, channelR, devicesR, locationsR, pagesR,
      audienceR, demographicsR, acquisitionR, pagesOverviewR,
      weeklyTrendR, channelsByDeviceR,
    ] = await Promise.allSettled([
      analyticsApi.overview(p),
      analyticsApi.channels(p),
      analyticsApi.devices(p),
      analyticsApi.locations(p),
      analyticsApi.pages(p),
      analyticsApi.audience(p),
      analyticsApi.demographics(p),
      analyticsApi.acquisition(p),
      analyticsApi.pagesOverview(p),
      analyticsApi.weeklyTrend(),
      analyticsApi.channelsByDevice(p),
    ]);

    if (!mountedRef.current) return;

    // Connected if overview succeeded — it's the definitive GA4 check
    const overview = ok(overviewR);
    const isConnected = !!(overview?.connected !== false && overview !== null);

    const channelRes = ok(channelR) || {};
    setData(prev => ({
      ...prev,
      overview,
      channels:        channelRes.channels      ?? prev.channels,
      channelTotals:   channelRes.totals        ?? prev.channelTotals,
      devices:         ok(devicesR)             ?? prev.devices,
      locations:       ok(locationsR)           ?? prev.locations,
      pages:           ok(pagesR)               ?? prev.pages,
      audience:        ok(audienceR)            ?? prev.audience,
      demographics:    ok(demographicsR)        ?? prev.demographics,
      acquisition:     ok(acquisitionR)         ?? prev.acquisition,
      pagesOverview:   ok(pagesOverviewR)       ?? prev.pagesOverview,
      weeklyTrend:     ok(weeklyTrendR)         ?? prev.weeklyTrend,
      channelsByDevice:ok(channelsByDeviceR)    ?? prev.channelsByDevice,
    }));
    setConnected(isConnected);
    setError(isConnected ? null : (overviewR.reason?.message || "GA4 not reachable"));
    setLoading(false);
  }, [start, end]);

  const fetchRealtime = useCallback(async () => {
    const [realtimeR, realtimeOverviewR, eventsR] = await Promise.allSettled([
      analyticsApi.realtime(),
      analyticsApi.realtimeOverview(),
      analyticsApi.events(),
    ]);
    if (!mountedRef.current) return;
    const ok = r => r.status === "fulfilled" ? r.value : null;
    setData(prev => ({
      ...prev,
      ...(ok(realtimeR)         && { realtime:         ok(realtimeR) }),
      ...(ok(realtimeOverviewR) && { realtimeOverview: ok(realtimeOverviewR) }),
      ...(ok(eventsR)           && { events:           ok(eventsR) }),
    }));
  }, []);

  const fetchPerPub = useCallback(async () => {
    const p = start ? { start, end } : {};
    const ok = r => r.status === "fulfilled" ? r.value : null;
    const [overviewR, channelsR, audienceR, engagementR, technologyR, dailyViewsR, hourlyViewsR] = await Promise.allSettled([
      analyticsApi.overviewPerPub(p),
      analyticsApi.channelsPerPub(p),
      analyticsApi.audiencePerPub(p),
      analyticsApi.engagementPerPub(p),
      analyticsApi.technologyPerPub(p),
      analyticsApi.dailyViewsPerPub(),
      analyticsApi.hourlyViewsPerPub(),
    ]);
    if (!mountedRef.current) return;
    setData(prev => ({
      ...prev,
      overviewPerPub:    ok(overviewR)    ?? prev.overviewPerPub,
      channelsPerPub:    ok(channelsR)    ?? prev.channelsPerPub,
      audiencePerPub:    ok(audienceR)    ?? prev.audiencePerPub,
      engagementPerPub:  ok(engagementR)  ?? prev.engagementPerPub,
      technologyPerPub:  ok(technologyR)  ?? prev.technologyPerPub,
      dailyViewsPerPub:  ok(dailyViewsR)  ?? prev.dailyViewsPerPub,
      hourlyViewsPerPub: ok(hourlyViewsR) ?? prev.hourlyViewsPerPub,
    }));
  }, [start, end]);

  const fetchRealtimePerPub = useCallback(async () => {
    const [r] = await Promise.allSettled([analyticsApi.realtimePerPub()]);
    if (!mountedRef.current) return;
    if (r.status === "fulfilled" && r.value) {
      setData(prev => ({ ...prev, realtimePerPub: r.value }));
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const full = setInterval(fetchAll, REFRESH_MS);
    return () => clearInterval(full);
  }, [fetchAll]);

  useEffect(() => {
    fetchRealtime();
    const rt = setInterval(fetchRealtime, REALTIME_MS);
    return () => clearInterval(rt);
  }, [fetchRealtime]);

  useEffect(() => {
    fetchPerPub();
    const full = setInterval(fetchPerPub, REFRESH_MS);
    return () => clearInterval(full);
  }, [fetchPerPub]);

  useEffect(() => {
    fetchRealtimePerPub();
    const rt = setInterval(fetchRealtimePerPub, 60 * 1000);
    return () => clearInterval(rt);
  }, [fetchRealtimePerPub]);

  useEffect(() => {
    let socket = null;
    try {
      // eslint-disable-next-line
      const { io } = require("socket.io-client");
      const SOCKET_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
      socket = io(SOCKET_URL, { reconnectionAttempts: 3, reconnectionDelay: 2000, timeout: 3000 });
      socket.on("gaRealtime", (rt) => {
        if (mountedRef.current) setData(prev => ({ ...prev, realtime: rt }));
      });
      socket.on("gaRealtimeOverview", (ro) => {
        if (mountedRef.current) setData(prev => ({ ...prev, realtimeOverview: ro }));
      });
    } catch (e) {}
    return () => { if (socket) socket.disconnect(); };
  }, []);

  return { data, loading, connected, error, refresh: fetchAll };
}
